import { createApiQuery } from "@/hooks/use-api-query";
import { get, post } from "@/libs/http";
import { useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/types/react-query';
import type { GiftPackageInfoReq, GiftPackageInfoRes } from "@/types/api/reward/gift-package-info";
import type { RewardCenterListRes } from "@/types/api/reward/reward-center-list.d";
import type { RewardPopupListRes } from "@/types/api/reward/reward-popup-list";
import type { RewardUnlockDetailsReq, RewardUnlockDetailsRes } from "@/types/api/reward/reward-unlock-details";
import type { UnloggedGiftPackageInfoRes } from "@/types/api/reward/unlogged-gift-package-info.d";
import type { RewardCenterCountReq, RewardCenterCountRes } from "@/types/api/reward/reward-center-count.d";
import type { ClaimRewardReq, ClaimRewardRes } from "@/types/api/reward/claim-reward";
import { createApiMutationHook } from "@/hooks/use-api-mutation";

export const getRewardCenterList = () => {
  return get<RewardCenterListRes>('/user/reward/v1/reward-center-list');
};

export const getUnloggedGiftPackageInfo = () => {
  return get<UnloggedGiftPackageInfoRes>('/user/reward/v1/unlogged-gift-package-info');
};

export const getRewardCenterCount = (params: RewardCenterCountReq, isPolling?: boolean) => {
  return get<RewardCenterCountRes>('/user/reward/v1/reward-center-count', {
    params,
    isPollingRequest: !!isPolling,
  } as any);
};

export const claimReward = ({ params }: { params: ClaimRewardReq; }) => {
  return post<ClaimRewardRes>('/user/reward/v1/claim-reward', params);
};

export const getRewardPopupList = () => {
  return get<RewardPopupListRes>('/user/reward/v1/reward-popup-list');
};

export const getRewardUnlockedDetail = (params: RewardUnlockDetailsReq) => {
  return get<RewardUnlockDetailsRes>('/user/reward/v1/reward-unlock-details', { params });
};

export const getGiftPackageInfo = (params: GiftPackageInfoReq) => {
  return get<GiftPackageInfoRes>('/user/reward/v1/gift-package-info', { params });
};

export const useGetRewardCenterList = createApiQuery(getRewardCenterList, (params) => ['reward-center-list', params]);
export const useGetUnloggedGiftPackageInfo = createApiQuery(getUnloggedGiftPackageInfo, (params) => ['unlogged-gift-package-info', params]);

// 重写 useGetRewardCenterCount 以支持轮询标记
const { getQueryOptions: getRewardCenterCountQueryOptions } = createApiQuery(
  (params: RewardCenterCountReq) => getRewardCenterCount(params, false), // 默认非轮询
  (params) => ['reward-center-count', params]
);

// 重写 useQueryHook 以支持轮询标记
const useQueryHook = (options: {
  params?: RewardCenterCountReq;
  queryConfig?: QueryConfig<() => ReturnType<typeof getRewardCenterCountQueryOptions>>;
}) => {
  const { queryConfig, params = {} } = options;
  const isPolling = !!queryConfig?.refetchInterval;
  
  // 重写 queryFn 以传递轮询标记
  const customQueryOptions = {
    ...getRewardCenterCountQueryOptions(params),
    queryFn: () => getRewardCenterCount(params, isPolling),
    ...queryConfig,
  };
  
  return useQuery(customQueryOptions);
};

export const useGetRewardCenterCount = {
  useQueryHook,
};

/**
 * @des 获取奖励弹窗列表 Hook (Query)
 * GET /user/reward/v1/reward-popup-list
 */
const { useNoCacheQueryHook: useGetRewardPopupList } = createApiQuery(getRewardPopupList, () => ['reward', 'reward-popup-list']);
export { useGetRewardPopupList };
/**
 * @description 获取奖励解锁详情 Hook
 * GET /user/reward/v1/reward-unlock-details
 */
const { useNoCacheQueryHook: useGetRewardUnlockedDetail } = createApiQuery(getRewardUnlockedDetail, (params) => ['reward', 'reward-unlock-details', params]);
export { useGetRewardUnlockedDetail };

/**
 * @description 获取礼包信息 Hook
 * GET /user/reward/v1/gift-package-info
 */
const { useNoCacheQueryHook: useGetGiftPackageInfo } = createApiQuery(getGiftPackageInfo, (params) => ['reward', 'gift-package-info', params]);
export { useGetGiftPackageInfo };

/**
 * @description 领取奖励 Hook
 * POST /user/reward/v1/claim-reward
 */
const useClaimRewardMutation = createApiMutationHook(claimReward);
export { useClaimRewardMutation };
